// src/features/ownerAdmin/CreatorLogsPage.tsx
import { useState, type FormEvent } from 'react';
import { useAllGuildLogs, useDeleteLogEntry, useClearAllLogs, type LogSort } from './useCreatorData';
import { GuildSearchInput, type GuildMatch } from './GuildSearchInput';
import { formatGuildName } from '../../lib/formatGuildName';
import { summarize } from '../logs/summarize';
import { formatRelative, formatExactDateTime } from '../../lib/formatTime';
import './OwnerAdminPage.css';

const SORT_OPTIONS: LogSort[] = ['newest', 'oldest', 'guild'];

export function CreatorLogsPage() {
  const [sort, setSort] = useState<LogSort>('newest');
  const [offset, setOffset] = useState(0);
  const [selectedGuild, setSelectedGuild] = useState<GuildMatch | null>(null);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const limit = 20;

  const { data, isLoading, error } = useAllGuildLogs(sort, offset, limit, selectedGuild?.guild_id, search);
  const deleteEntry = useDeleteLogEntry();
  const clearAll = useClearAllLogs();
  const [confirmingClear, setConfirmingClear] = useState(false);

  const total = data?.total ?? 0;
  const hasNext = offset + limit < total;
  const hasPrev = offset > 0;

  function handleClearAll() {
    if (!confirmingClear) { setConfirmingClear(true); return; }
    clearAll.mutate(undefined, { onSuccess: () => setConfirmingClear(false) });
  }

  function handleSearchSubmit(e: FormEvent) {
    e.preventDefault();
    setSearch(searchInput);
    setOffset(0);
  }

  return (
    <>
      <div className="page-header">
        <p className="page-header__eyebrow">All servers — bot owner only</p>
        <h1 className="page-header__title">Logs</h1>
      </div>

      <article className="card">
        <div className="card__content">
          {/* Sort by type — same button-row pattern as the regular
              dashboard's filter UIs (Bug Reports, Market Alerts). */}
          <div className="owner-admin-filter">
            {SORT_OPTIONS.map((s) => (
              <button key={s} className={sort === s ? 'button-primary' : 'button-secondary'} onClick={() => { setSort(s); setOffset(0); }}>
                {s === 'guild' ? 'By server' : s}
              </button>
            ))}
          </div>

          {/* Guild filter (search-as-you-type, not a plain dropdown —
              built to stay usable well past 500 servers) plus a text
              search across action type, user, and target. */}
          <div style={{ display: 'flex', gap: '1rem', margin: '0.75rem 0', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 220 }}>
              <GuildSearchInput selectedGuild={selectedGuild} onSelect={(g) => { setSelectedGuild(g); setOffset(0); }} />
            </div>
            <form onSubmit={handleSearchSubmit} style={{ display: 'flex', gap: '0.5rem', flex: 1, minWidth: 220 }}>
              <input
                className="input"
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search action, user, or target…"
              />
              <button type="submit" className="button-secondary">Search</button>
              {search && (
                <button type="button" className="button-secondary" onClick={() => { setSearch(''); setSearchInput(''); setOffset(0); }}>
                  Clear
                </button>
              )}
            </form>
          </div>

          <div className="owner-admin-filter" style={{ justifyContent: 'flex-end', marginBottom: '0.75rem' }}>
            <button className="button-danger" onClick={handleClearAll} disabled={clearAll.isPending || total === 0}>
              {confirmingClear ? 'Click again to confirm' : 'Clear all logs'}
            </button>
          </div>

          {isLoading && <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Loading…</p>}
          {error && <p style={{ color: 'var(--negative)', fontSize: 13 }}>Couldn&apos;t load logs.</p>}
          {data?.entries.length === 0 && <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>No logs match the current filters.</p>}

          {data && data.entries.length > 0 && (
            <>
              <table className="owner-admin-table">
                <thead><tr><th>When</th><th>Server</th><th>Who</th><th>What changed</th><th></th></tr></thead>
                <tbody>
                  {data.entries.map((entry, index) => (
                    <tr key={entry.id} className={index % 2 === 1 ? 'zebra-row--even' : undefined}>
                      <td className="owner-admin-table__mono">
                        {formatRelative(entry.created_at)}
                        <span style={{ opacity: 0.7, fontSize: 11 }}> · {formatExactDateTime(entry.created_at)}</span>
                      </td>
                      <td>{entry.guild_id ? formatGuildName(entry.guild_name, entry.guild_id) : '—'}</td>
                      <td>{entry.user_display_name ?? entry.user_id}</td>
                      <td>{summarize(entry)}</td>
                      <td>
                        <button className="button-danger" onClick={() => deleteEntry.mutate(entry.id)} disabled={deleteEntry.isPending}>
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="logs-pagination">
                <button className="button-secondary" onClick={() => setOffset(Math.max(0, offset - limit))} disabled={!hasPrev}>Newer</button>
                <span className="logs-pagination__label">{offset + 1}–{Math.min(offset + limit, total)} of {total}</span>
                <button className="button-secondary" onClick={() => setOffset(offset + limit)} disabled={!hasNext}>Older</button>
              </div>
            </>
          )}
        </div>
      </article>
    </>
  );
}
